import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { PasswordInput, Button, Paper, Title, Stack, Alert, Anchor, Text } from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useState } from 'react';
import { AuthApi } from '../api';
import {
  ResetPasswordFormSchema,
  type ResetPasswordFormData,
} from '../schemas/ResetPasswordForm.schema';
import { ApiError } from '@/shared/api/ApiError';
import { AuthScreenLayout } from './AuthScreenLayout';

interface ResetPasswordSearch {
  code?: string;
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as ResetPasswordSearch;
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const resetCode = search.code;

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(ResetPasswordFormSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!resetCode) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    setError(null);
    try {
      await AuthApi.resetPassword({
        resetCode,
        newPassword: data.newPassword,
      });
      setSuccess(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || 'Failed to reset password. The link may have expired.');
      } else {
        setError('An unexpected error occurred');
      }
    }
  };

  if (!resetCode) {
    return (
      <AuthScreenLayout>
        <Title ta="center" order={2}>
          Invalid reset link
        </Title>

        <Paper withBorder shadow="md" p={30} mt={30} radius="md">
          <Stack>
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              This password reset link is invalid or has expired.
            </Alert>

            <Button onClick={() => navigate({ to: '/forgot-password' })}>
              Request new reset link
            </Button>

            <Anchor
              component="button"
              type="button"
              size="sm"
              ta="center"
              onClick={() => navigate({ to: '/login' })}
            >
              Back to login
            </Anchor>
          </Stack>
        </Paper>
      </AuthScreenLayout>
    );
  }

  if (success) {
    return (
      <AuthScreenLayout>
        <Title ta="center" order={2}>
          Password reset successful
        </Title>

        <Paper withBorder shadow="md" p={30} mt={30} radius="md">
          <Stack>
            <Alert icon={<IconCheck size={16} />} color="green" variant="light">
              Your password has been updated successfully.
            </Alert>

            <Button onClick={() => navigate({ to: '/login' })}>
              Sign in with new password
            </Button>
          </Stack>
        </Paper>
      </AuthScreenLayout>
    );
  }

  return (
    <AuthScreenLayout>
      <Title ta="center" order={2}>
        Reset your password
      </Title>
      <Text c="dimmed" size="sm" ta="center" mt={5}>
        Enter your new password below
      </Text>

      <Paper withBorder shadow="md" p={30} mt={30} radius="md">
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Stack>
            {error && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                {error}
              </Alert>
            )}

            <PasswordInput
              label="New Password"
              placeholder="Enter new password"
              {...form.register('newPassword')}
              error={form.formState.errors.newPassword?.message}
            />

            <PasswordInput
              label="Confirm Password"
              placeholder="Confirm new password"
              {...form.register('confirmPassword')}
              error={form.formState.errors.confirmPassword?.message}
            />

            <Button type="submit" fullWidth loading={form.formState.isSubmitting}>
              Reset password
            </Button>

            <Anchor
              component="button"
              type="button"
              size="sm"
              ta="center"
              onClick={() => navigate({ to: '/login' })}
            >
              Back to login
            </Anchor>
          </Stack>
        </form>
      </Paper>
    </AuthScreenLayout>
  );
}
